import { useEffect, useRef, useState } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { walletApi, type WalletKind, type WalletTransaction } from '@/api/wallet';
import { TransactionRow } from '@/components/wallet/TransactionRow';
import { Button } from '@/components/ui/Button';
import { Display, Eyebrow } from '@/components/ui/Typography';
import { friendlyMessage } from '@/lib/errors';

const PAGE_SIZE = 20;

const CASHBACK_GRADIENT = 'linear-gradient(135deg,#835DD0,#C04CAE)';

function dayLabel(dateStr: string): string {
  const date = new Date(dateStr);
  if (isToday(date)) return 'Hoje';
  if (isYesterday(date)) return 'Ontem';
  const raw = format(date, 'EEE, d MMM', { locale: ptBR }).replace(/\./g, '');
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

interface DayGroup {
  key: string;
  label: string;
  items: WalletTransaction[];
}

/** Agrupa preservando a ordem (a API devolve desc por created_at). */
function groupByDay(transactions: WalletTransaction[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const tx of transactions) {
    const key = format(new Date(tx.created_at), 'yyyy-MM-dd');
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(tx);
    } else {
      groups.push({ key, label: dayLabel(tx.created_at), items: [tx] });
    }
  }
  return groups;
}

export default function History() {
  const [tab, setTab] = useState<WalletKind>('cash');
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const tabRef = useRef(tab);

  async function loadPage(fromOffset: number, forTab: WalletKind) {
    setLoading(true);
    setError(null);
    try {
      const resp = await walletApi.transactions(PAGE_SIZE, fromOffset, forTab);
      if (tabRef.current !== forTab) return; // trocou de aba durante o fetch
      setTransactions((prev) =>
        fromOffset === 0 ? resp.items : [...prev, ...resp.items]
      );
      setTotal(resp.total);
      setOffset(fromOffset + resp.items.length);
    } catch (err) {
      if (tabRef.current !== forTab) return;
      setError(friendlyMessage(err, 'Falha ao carregar'));
    } finally {
      if (tabRef.current === forTab) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  }

  useEffect(() => {
    tabRef.current = tab;
    setTransactions([]);
    setTotal(null);
    setOffset(0);
    setInitialLoad(true);
    loadPage(0, tab);
    // loadPage é estável pelo escopo da função; refaz a 1ª página a cada aba
  }, [tab]);

  const hasMore = total !== null && offset < total;
  const groups = groupByDay(transactions);

  return (
    <div className="flex flex-col gap-5">
      <header className="pt-1">
        <Eyebrow>Suas movimentações</Eyebrow>
        <Display size={28} className="mt-1">
          Histórico
        </Display>
        {total !== null && (
          <p className="mt-1 text-sm text-bow-royal/55">
            {total} {total === 1 ? 'movimentação' : 'movimentações'}
          </p>
        )}
      </header>

      <div
        className="flex rounded-full bg-white p-1"
        style={{ border: '1px solid rgba(0,27,116,0.10)' }}
      >
        <button
          type="button"
          onClick={() => setTab('cash')}
          aria-pressed={tab === 'cash'}
          className="h-10 flex-1 rounded-full text-sm font-semibold transition-colors"
          style={
            tab === 'cash'
              ? { background: '#001B74', color: '#F1EDE4' }
              : { background: 'transparent', color: 'rgba(0,27,116,0.6)' }
          }
        >
          Saldo
        </button>
        <button
          type="button"
          onClick={() => setTab('bonus')}
          aria-pressed={tab === 'bonus'}
          className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full text-sm font-semibold transition-colors"
          style={
            tab === 'bonus'
              ? { background: CASHBACK_GRADIENT, color: '#F1EDE4' }
              : { background: 'transparent', color: 'rgba(0,27,116,0.6)' }
          }
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 12,
              lineHeight: 1
            }}
          >
            $
          </span>
          Cashback
        </button>
      </div>

      {error && (
        <div className="rounded-3xl bg-white p-5 shadow-soft">
          <p className="text-sm text-bow-red">{error}</p>
        </div>
      )}

      {initialLoad && loading && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-2xl bg-white shadow-soft" />
          ))}
        </div>
      )}

      {!initialLoad && !error && transactions.length === 0 && (
        <div className="rounded-3xl bg-white p-5 shadow-soft">
          <p className="text-sm text-bow-royal/65">Nenhuma movimentação ainda.</p>
        </div>
      )}

      {groups.length > 0 && (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-2">
              <span
                className="px-1 text-[11px] font-semibold uppercase tracking-[0.06em]"
                style={{ color: 'rgba(0,27,116,0.4)' }}
              >
                {group.label}
              </span>
              <div className="rounded-[20px] bg-white px-4 shadow-soft">
                <div className="divide-y divide-bow-royal/5">
                  {group.items.map((tx) => (
                    <TransactionRow
                      key={tx.id}
                      tx={tx}
                      pointName={tx.point_name ?? undefined}
                      energyKwh={tx.energy_kwh ?? undefined}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <Button
          variant="secondary"
          onClick={() => loadPage(offset, tab)}
          disabled={loading}
        >
          {loading ? 'Carregando…' : 'Carregar mais'}
        </Button>
      )}
    </div>
  );
}
